/**
 * 문의·프로필에서 공통으로 쓰는 선택지.
 *
 * 교육 문의·B2B 문의·가입 온보딩이 **같은 축**을 써야 나중에 세그먼트가 합쳐진다
 * → 13. LEAD-01 §6.2. 화면마다 따로 정의하면 「제조」와 「제조업」이 갈린다.
 *
 * 업무 항목을 늘릴 때는 **서로 겹치지 않게** 나눈다. 「보고」와 「문서 작성」이 서로를 덮으면
 * 고르는 사람마다 다른 뜻으로 체크해 세그먼트가 흐려진다.
 * 목록이 길어지는 부담보다, 자기 업무가 없어서 「기타」로 몰리는 쪽이 더 나쁘다.
 */
export const WORK_TYPES = [
  '주간·월간 보고',
  '회의록·녹취 정리',
  '데이터 집계·분석',
  '문서 작성·검토',
  '고객 응대·문의 처리',
  '견적·제안서',
  '일정·예약 관리',
  '재고·발주 관리',
  '정산·경비 처리',
  '채용·인사 업무',
  '마케팅 콘텐츠·SNS',
  '기타',
] as const;

export const INDUSTRIES = ['제조', '서비스업', '유통', 'IT·스타트업', '기타'] as const;

export const SIZES = ['1~9명', '10~49명', '50~199명', '200명 이상'] as const;

/**
 * 교육 문의 전용 — 누가 묻는지에 따라 뒤 대응이 완전히 달라진다.
 * 기업(B2B)과 공공(B2G)은 계약·정산·증빙 절차가 아예 다르므로 첫 칸에서 갈라 둔다.
 */
export const INQUIRY_TYPES = [
  '기업 교육',
  '공공기관·지자체 교육',
  '학교·협회 등 단체 교육',
  '개인 수강 문의',
] as const;

export const HEADCOUNTS = ['1~5명', '6~20명', '21~50명', '50명 이상', '미정'] as const;

export const TIMEFRAMES = ['1개월 이내', '1~3개월', '3개월 이후', '미정'] as const;

/**
 * 예산 범위.
 *
 * 액수를 정확히 묻지 않고 **구간으로만** 받는다 — 정확한 숫자를 요구하면 아직 못 정한 사람이
 * 폼을 닫는다. 우리에게 필요한 것도 정확한 숫자가 아니라 「어느 규모로 구성할지」다.
 *
 * 마지막 항목이 없으면 안 된다. 랜딩이 「정해서 오실 필요 없습니다」라고 말해 놓고
 * 폼에서 예산을 강제하면 앞말이 거짓이 된다.
 */
export const BUDGET_RANGES = [
  '500만원 미만',
  '500만 ~ 1,000만원',
  '1,000만 ~ 3,000만원',
  '3,000만 ~ 5,000만원',
  '5,000만원 이상',
  '아직 정하지 못했습니다',
] as const;

/**
 * 직무 — 업종보다 **자료 주제를 잘 예측하는 축**이다.
 * 제조업 마케터와 IT 마케터는 업종이 달라도 같은 자료를 쓴다.
 *
 * 「학생·취업 준비 중」·「프리랜서」를 여기 함께 둔 이유: 직업 상태를 따로 묻지 않기 위해서다.
 * 질문 하나를 통째로 없애면서 조직 소속 여부까지 같이 판정한다.
 */
export const JOB_ROLES = [
  '기획·전략',
  '마케팅',
  '영업',
  '인사·총무',
  '재무·회계',
  '고객지원',
  '개발',
  '디자인',
  '대표·임원',
  '온라인 셀러',
  '프리랜서',
  '학생·취업 준비 중',
  '기타',
] as const;

export type JobRole = (typeof JOB_ROLES)[number];

/**
 * 닉네임 앞에 붙는 짧은 직무 태그 — `회계#민지`.
 *
 * `JOB_ROLES` 는 세그먼트를 가르는 축이라 「재무·회계」처럼 길고 정확한 라벨을 쓴다.
 * 그 라벨을 닉네임에 그대로 넣으면 `재무·회계#민지` 가 되어 채팅 목록에서 이름이 밀린다.
 * 그래서 **표시용 짧은 이름**을 여기서 1:1 로 붙인다. 화면에서 즉석으로 잘라 쓰면
 * 「재무」와 「회계」로 갈리므로, 자르지 말고 이 표를 유일한 출처로 삼는다.
 *
 * 「기타」에만 값이 없다. 본인이 직접 태그를 적는 자리이기 때문이다.
 */
export const JOB_ROLE_TAGS: Readonly<Record<JobRole, string>> = {
  기획·전략: '기획',
  마케팅: '마케팅',
  영업: '영업',
  인사·총무: '인사',
  재무·회계: '회계',
  고객지원: 'CS',
  개발: '개발',
  디자인: '디자인',
  대표·임원: '대표',
  '온라인 셀러': '셀러',
  프리랜서: '프리랜서',
  '학생·취업 준비 중': '학생',
  기타: '',
};

/**
 * 온보딩 2단계 — 지금 가장 반복적으로 시간을 뺏는 업무 **하나**.
 *
 * `WORK_TYPES`(12개)와 목적이 다르다. 저쪽은 교육 문의에서 **범위를 빠짐없이 적는** 자리라
 * 길어도 되지만, 여기는 가입 직후 5초 안에 하나를 고르게 하는 자리다. 보기가 열 개를 넘으면
 * 읽다가 창을 닫는다. 그래서 겹치지 않는 **다섯 개**로 좁히고 나머지는 「기타」가 받는다.
 *
 * 이 값이 소분방 배정과 콘텐츠 추천의 근거가 되므로, 항목을 늘릴 때는 서로 덮지 않는지부터 본다.
 * 「보고」와 「문서 작성」이 겹치면 고르는 사람마다 다른 뜻으로 체크해 세그먼트가 흐려진다.
 */
/**
 * 「기타」 — 보기 목록이 못 잡는 것을 받는 자리.
 *
 * 문자열을 화면마다 다시 적지 않는다. 이 값은 **저장된 값을 되읽을 때의 기준**이기도 해서
 * (`entities/session/lib/time-sink.ts`), 한 글자만 달라져도 되읽기가 조용히 어긋난다.
 */
export const OTHER = '기타';

export const TIME_SINKS = [
  '보고서·자료 취합',
  '데이터 입력·정리',
  '고객·문의 응대',
  '일정·예약·발주 관리',
  '정산·경비 처리',
] as const;

/**
 * 온보딩 3단계 — 자동화 경험 수준. **콘텐츠 난이도를 맞추는 용도**다.
 *
 * 입문자에게 고급 내용을 던지면 이탈하고, 이미 툴을 쓰는 사람에게 기초만 주면 흥미를 잃는다.
 * 도구 이름을 그대로 적어 자기 위치를 고민 없이 짚게 한다 — 「초급/중급」 같은 자기평가는
 * 사람마다 기준이 달라 값이 안 맞는다.
 */
export const AUTOMATION_LEVELS = [
  '아직 안 씁니다',
  '엑셀·매크로 정도 씁니다',
  'ChatGPT 같은 AI 도구를 씁니다',
  '이미 전문 툴을 도입했습니다',
] as const;

/** 니즈 강도의 대리 지표. 시간이 많이 새는 사람이 유료로 넘어간다. */
export const WEEKLY_REPEAT_HOURS = [
  '주 1~3시간',
  '주 4~10시간',
  '주 10시간 이상',
  '잘 모르겠다',
] as const;
